import { type CSSProperties, type MouseEvent as ReactMouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Loader2,
  Play,
  Send,
  RotateCcw,
  Lock,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  LogIn,
  Database,
  Table2,
  Plus,
  Trash2,
  Circle,
  Eye,
  EyeOff,
} from 'lucide-react';
import { SqlEditor } from '@/components/editor/SqlEditor';
import { ResultTable } from '@/components/editor/ResultTable';
import { SampleTables } from '@/components/problems/SampleTables';
import { DiscussionTab } from '@/components/problems/DiscussionTab';
import { EditorialTab } from '@/components/problems/EditorialTab';
import { SolutionsTab } from '@/components/problems/SolutionsTab';
import { DifficultyBadge } from '@/components/ui/DifficultyBadge';
import { MarkdownLite } from '@/components/ui/MarkdownLite';
import { Button } from '@/components/ui/Button';
import { ClientOnly } from '@/components/ClientOnly';
import { contestsApi, problemsApi, submissionsApi } from '@/api/endpoints';
import { getApiErrorMessage } from '@/api/client';
import { mapDifficulty } from '@/api/mappers';
import type {
  ApiSqlDialect,
  CustomTablePayload,
  ProblemDetailResponse,
  ProblemSummaryResponse,
  SampleTestCaseResponse,
  SubmissionResponse,
} from '@/api/types';
import { useAuthStore } from '@/store/auth';
import { useToastStore } from '@/store/toast';
import { useLocalized } from '@/hooks/useLocalized';
import { useSeo } from '@/hooks/useSeo';
import { useInitialData } from '@/ssr/initialData';
import { primaryTopic as pickPrimaryTopic, topicBySlug, topicPath } from '@/data/topics';
import { pickRelated } from '@/lib/related';
import { problemSeoDescription, problemSeoTitle } from '@/lib/seoText';
import { PAYWALL_CLASS, TOPICS_INDEX_PATH, problemStructuredData } from '@/lib/structuredData';
import { difficultyPath } from '@/data/difficulties';
import { NotFound } from './NotFound';
import type { QueryResult } from '@/types';
import { cn } from '@/lib/cn';
import { timeAgo } from '@/lib/format';
import { parseSetupSql } from '@/lib/parseSetupSql';

// "Yechimlar" — rasmiy yechim kodi (jamoa yechimlari emas).
type Tab = 'description' | 'editorial' | 'solutions' | 'submissions' | 'discuss';

const DIALECT_LABEL: Record<ApiSqlDialect, string> = {
  POSTGRESQL: 'PostgreSQL',
  MYSQL: 'MySQL',
  SQLITE: 'SQLite',
  MSSQL: 'SQL Server',
  ORACLE: 'Oracle',
};

type Verdict = 'ACCEPTED' | 'WRONG_ANSWER' | 'RUNTIME_ERROR' | 'SYNTAX_ERROR' | 'TIME_LIMIT' | 'OUTPUT';

interface ConsoleResult {
  status: string;
  runtimeMs: number | null;
  message: string | null;
  resultDiff: unknown;
}

const verdictIcon: Record<Verdict, typeof CheckCircle2> = {
  ACCEPTED: CheckCircle2,
  WRONG_ANSWER: XCircle,
  RUNTIME_ERROR: AlertTriangle,
  SYNTAX_ERROR: AlertTriangle,
  TIME_LIMIT: Clock,
  OUTPUT: Table2,
};
const verdictColor: Record<Verdict, string> = {
  ACCEPTED: 'var(--success)',
  WRONG_ANSWER: 'var(--error)',
  RUNTIME_ERROR: 'var(--warning)',
  SYNTAX_ERROR: 'var(--warning)',
  TIME_LIMIT: 'var(--warning)',
  OUTPUT: 'var(--brand-secondary)',
};

/** setup_sql ni sxema (CREATE ...) va ma'lumot (INSERT ...) qismlariga ajratadi. */
/** Custom sinash uchun tahrirlanadigan jadval holati (yacheykalar matn; '' = NULL). */
interface CustomTableState {
  name: string;
  columns: Array<{ name: string; type: string }>;
  rows: string[][];
}

/** Namuna setup SQL dan tahrirlanadigan jadvallar (ustunlar + oldindan to'ldirilgan qatorlar). */
function buildCustomTables(setupSql: string | null | undefined): CustomTableState[] {
  return parseSetupSql(setupSql).map((tbl) => ({
    name: tbl.name,
    columns: tbl.columns.map((c) => ({ name: c.name, type: c.type })),
    rows: tbl.rows.length > 0
      ? tbl.rows.map((r) => r.map(cellToText))
      : [tbl.columns.map(() => '')],
  }));
}

function cellToText(v: string | number | null): string {
  return v == null ? '' : String(v);
}

/** Grid holatini backend payloadiga (bo'sh qatorlar tashlanadi). */
function customTablesToPayload(tables: CustomTableState[]): CustomTablePayload[] {
  return tables.map((t) => ({
    name: t.name,
    columns: t.columns,
    rows: t.rows
      .filter((row) => row.some((c) => c.trim() !== ''))
      .map((row) => row.map((c) => (c.trim() === '' ? null : c))),
  }));
}

/** `[{col: val}, ...]` shaklidagi qatorlarni ResultTable kutgan {columns, rows} ga o'giradi. */
function rowsToQueryResult(rows: unknown): QueryResult | null {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const first = rows[0] as Record<string, unknown>;
  const columns = Object.keys(first);
  return {
    columns,
    rows: rows.map((r) => columns.map((c) => (r as Record<string, unknown>)[c] as string | number | null)),
  };
}

function getDiffPreview(resultDiff: unknown, key: 'expectedPreview' | 'actualPreview'): QueryResult | null {
  if (!resultDiff || typeof resultDiff !== 'object') return null;
  const preview = (resultDiff as Record<string, unknown>)[key];
  // Judge `toPreview` `{ columns, rows: [{col: val}] }` obyektini qaytaradi — qatorlar
  // massivini ajratamiz. Eski/xom massiv ko'rinishini ham qo'llab-quvvatlaymiz.
  const rows =
    preview && typeof preview === 'object' && !Array.isArray(preview) && 'rows' in (preview as object)
      ? (preview as Record<string, unknown>).rows
      : preview;
  return rowsToQueryResult(rows);
}

/** Birinchi yiqilgan testning kirish ma'lumoti (setup SQL) — judge oshkor qilsa. */
function getFailedInput(resultDiff: unknown): string | null {
  if (!resultDiff || typeof resultDiff !== 'object') return null;
  const v = (resultDiff as Record<string, unknown>).failedInput;
  return typeof v === 'string' && v.trim() !== '' ? v : null;
}

interface FailedDetails {
  input: string | null;
  actual: QueryResult | null;
  expected: QueryResult | null;
}

/** Birinchi yiqilgan test tafsilotlari (kirish + olingan + kutilgan) — judge oshkor qilsa. */
function getFailedDetails(resultDiff: unknown): FailedDetails | null {
  const input = getFailedInput(resultDiff);
  const actual = getDiffPreview(resultDiff, 'actualPreview');
  const expected = getDiffPreview(resultDiff, 'expectedPreview');
  if (!input && !actual && !expected) return null;
  return { input, actual, expected };
}

type DatasetStatus = 'passed' | 'failed' | 'pending';
interface DatasetItem {
  name: string;
  sample: boolean;
  status: DatasetStatus;
}
interface DatasetSummary {
  passed: number;
  total: number;
  datasets: DatasetItem[];
}

/** Ko'p-dataset judge natijasidan progress (passed/total + har bir dataset holati) ajratadi. */
function getDatasetSummary(resultDiff: unknown): DatasetSummary | null {
  if (!resultDiff || typeof resultDiff !== 'object') return null;
  const d = resultDiff as Record<string, unknown>;
  if (typeof d.total !== 'number' || !Array.isArray(d.datasets)) return null;
  const datasets: DatasetItem[] = d.datasets.map((raw) => {
    const it = raw as Record<string, unknown>;
    return {
      name: typeof it.name === 'string' ? it.name : '',
      sample: it.sample === true,
      status: (it.status === 'passed' || it.status === 'failed' ? it.status : 'pending') as DatasetStatus,
    };
  });
  return { passed: typeof d.passed === 'number' ? d.passed : 0, total: d.total, datasets };
}

const datasetStatusIcon: Record<DatasetStatus, typeof CheckCircle2> = {
  passed: CheckCircle2,
  failed: XCircle,
  pending: Circle,
};
const datasetStatusColor: Record<DatasetStatus, string> = {
  passed: 'var(--success)',
  failed: 'var(--error)',
  pending: 'var(--content-muted)',
};

/** Yiqilgan test tafsiloti: kirish ma'lumoti + olingan/kutilgan natija (ochilganda). */
function FailedTestDetail({
  details,
  onRunWithData,
}: {
  details: FailedDetails;
  onRunWithData?: (setupSql: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3 border-t border-line bg-elevated/40 px-3 py-3">
      {details.input && (
        <div>
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-content-muted">
              {t('problem.failedInput')}
            </span>
            {onRunWithData && (
              <button
                type="button"
                onClick={() => onRunWithData(details.input!)}
                className="btn btn-ghost gap-1.5 px-2 py-1 text-xs"
                title={t('problem.runOnFailedDataHint')}
              >
                <Play className="h-3.5 w-3.5" />
                {t('problem.runOnFailedData')}
              </button>
            )}
          </div>
          <SampleTables setupSql={details.input} />
        </div>
      )}
      {details.actual && (
        <div>
          <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-content-muted">
            {t('problem.yourOutput')}
          </div>
          <ResultTable result={details.actual} />
        </div>
      )}
      {details.expected && (
        <div>
          <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-content-muted">
            {t('problem.expected')}
          </div>
          <ResultTable result={details.expected} />
        </div>
      )}
    </div>
  );
}

/**
 * LeetCode uslubidagi test-dataset ro'yxati: har biri nomi + ✓/✗/○ holati bilan.
 * Yiqilgan test yonidagi ko'z tugmasi bosilsa uning tafsiloti (kirish + olingan/kutilgan)
 * o'sha qatorning ostida ochiladi.
 */
function DatasetChecklist({
  summary,
  details,
  onRunWithData,
}: {
  summary: DatasetSummary;
  details?: FailedDetails | null;
  onRunWithData?: (setupSql: string) => void;
}) {
  const { t } = useTranslation();
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="mb-4">
      <div className="mb-1.5 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-content-muted">
        <span>{t('problem.datasetsTitle')}</span>
        <span className="tnum normal-case">
          {t('problem.casesPassed', { passed: summary.passed, total: summary.total })}
        </span>
      </div>
      <ul className="divide-y divide-line overflow-hidden rounded-DEFAULT border border-line">
        {summary.datasets.map((ds, i) => {
          const Icon = datasetStatusIcon[ds.status];
          const canReveal = ds.status === 'failed' && !!details;
          return (
            <li key={i} className="bg-surface text-sm">
              <div className="flex items-center gap-2.5 px-3 py-2">
                <Icon className="h-4 w-4 shrink-0" style={{ color: datasetStatusColor[ds.status] }} />
                <span className="tnum text-xs text-content-muted">#{i + 1}</span>
                <span className={cn('truncate', ds.status === 'pending' && 'text-content-muted')}>
                  {ds.name || `Test ${i + 1}`}
                </span>
                {canReveal ? (
                  <button
                    type="button"
                    onClick={() => setRevealed((r) => !r)}
                    aria-expanded={revealed}
                    className="ml-auto inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-xs text-error transition-colors hover:bg-error/10"
                  >
                    {revealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    {revealed ? t('problem.datasetHide') : t('problem.datasetReveal')}
                  </button>
                ) : (
                  !ds.sample && (
                    <span className="ml-auto inline-flex shrink-0 items-center gap-1 text-xs text-content-muted">
                      <EyeOff className="h-3.5 w-3.5" />
                      {t('problem.datasetHidden')}
                    </span>
                  )
                )}
              </div>
              {canReveal && revealed && details && (
                <FailedTestDetail details={details} onRunWithData={onRunWithData} />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Masala sahifasi uchun schema.org: BreadcrumbList + LearningResource. */
export function Problem() {
  const { slug = '', contestSlug } = useParams();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuthStore();
  const push = useToastStore((s) => s.push);
  const { l, lang } = useLocalized();
  const inContest = Boolean(contestSlug);

  // Prerender qilingan sahifa: masala HTML ichida keladi — hydration shu bilan
  // (loading holatisiz) render qilinadi, API ga qayta so'rov yuborilmaydi.
  const initial = useInitialData();
  const initialProblem = !inContest && initial?.problem?.slug === slug ? initial.problem : null;

  const [problem, setProblem] = useState<ProblemDetailResponse | null>(initialProblem);
  const [related, setRelated] = useState<ProblemSummaryResponse[]>(
    initialProblem ? initial?.related ?? [] : [],
  );
  const [loading, setLoading] = useState(!initialProblem);
  const [notFound, setNotFound] = useState(false);
  const problemSlugRef = useRef<string | null>(initialProblem?.slug ?? null);
  /** Qaysi (kontest, slug, til) uchun masala yuklangan — takroriy fetch bo'lmasin. */
  const loadedKey = useRef<string | null>(initialProblem ? `|${slug}|${initial?.lang}` : null);

  const [tab, setTab] = useState<Tab>('description');
  const [dialect, setDialect] = useState<ApiSqlDialect>(
    () =>
      (initialProblem?.dialects.find((d) => d.isDefault) ?? initialProblem?.dialects[0])?.dialect ??
      'POSTGRESQL',
  );
  const [code, setCode] = useState('');
  const [running, setRunning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ConsoleResult | null>(null);
  // Qo'lda ma'lumot bilan sinash: sxema qotib turadi, faqat INSERT lar tahrirlanadi.
  const [customOpen, setCustomOpen] = useState(false);
  const [customTables, setCustomTables] = useState<CustomTableState[]>(() =>
    buildCustomTables(initialProblem?.sampleTestCases?.[0]?.setupSql),
  );
  const [customRunning, setCustomRunning] = useState(false);
  /** Submit tugagach oshadi — "Jo'natmalar" tabi ro'yxatni qayta yuklaydi. */
  const [submitCount, setSubmitCount] = useState(0);

  // Split pane
  const [leftWidth, setLeftWidth] = useState(45);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  // Editor / konsol vertikal split (konsol balandligi, % — o'ng panelga nisbatan)
  const [consoleHeight, setConsoleHeight] = useState(38);
  const vDragging = useRef(false);

  useEffect(() => {
    const key = `${contestSlug ?? ''}|${slug}|${lang}`;
    if (loadedKey.current === key) {
      // Yuklangan masalaga qaytildi (masalan, 404 sahifadan) — uni ko'rsatamiz
      setNotFound(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setNotFound(false);

    const detailPromise = inContest
      ? contestsApi.problemDetail(contestSlug!, slug, lang)
      : problemsApi.detail(slug, lang);

    detailPromise
      .then((res) => {
        if (cancelled) return;
        loadedKey.current = key;
        // Faqat til almashgan bo'lsa (shu masala) — yozilgan kod va natija saqlanadi
        if (problemSlugRef.current !== res.data.slug) {
          const defaultDialect = res.data.dialects.find((d) => d.isDefault) ?? res.data.dialects[0];
          setDialect(defaultDialect?.dialect ?? 'POSTGRESQL');
          setCode('');
          setResult(null);
          setCustomTables(buildCustomTables(res.data.sampleTestCases?.[0]?.setupSql));
          setCustomOpen(false);
        }
        problemSlugRef.current = res.data.slug;
        setProblem(res.data);
      })
      .catch(() => {
        // Shu masala allaqachon ko'rsatilgan (masalan, faqat til almashgan) — mavjud
        // kontentni 404 bilan almashtirmaymiz
        if (!cancelled && problemSlugRef.current !== slug) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug, lang, contestSlug, inContest]);

  // O'xshash masalalar (SPA navigatsiyasi; prerender sahifada tayyor keladi)
  // Summary'larda uch tildagi sarlavha bor — til almashganda qayta yuklash shart emas
  const relatedFor = useRef<string | null>(initialProblem ? slug : null);
  useEffect(() => {
    if (inContest || !problem || problem.slug !== slug) return;
    if (relatedFor.current === problem.slug) return;
    relatedFor.current = problem.slug;
    const primary = pickPrimaryTopic(problem.topics)?.slug;
    if (!primary) {
      setRelated([]);
      return;
    }
    let cancelled = false;
    problemsApi
      .list({ topic: primary, size: 100, lang })
      .then((res) => {
        if (!cancelled) setRelated(pickRelated(problem, res.data.content));
      })
      .catch(() => {
        if (!cancelled) setRelated([]);
      });
    return () => {
      cancelled = true;
    };
  }, [problem, slug, lang, inContest]);

  const startDrag = useCallback(() => {
    dragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMove = (e: MouseEvent) => {
      if (!dragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const pct = ((e.clientX - rect.left) / rect.width) * 100;
      setLeftWidth(Math.min(68, Math.max(28, pct)));
    };
    const onUp = () => {
      dragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  // Editor va konsol orasidagi vertikal resizer: konsol balandligini o'zgartiradi.
  // Konteynerni resizer'ning o'z parent'idan olamiz (desktop/mobil ikkala nusxa
  // uchun ham to'g'ri, ref chalkashligisiz).
  const startVDrag = useCallback((e: ReactMouseEvent<HTMLDivElement>) => {
    const container = e.currentTarget.parentElement;
    if (!container) return;
    vDragging.current = true;
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';

    const onMove = (ev: MouseEvent) => {
      if (!vDragging.current) return;
      const rect = container.getBoundingClientRect();
      if (rect.height === 0) return;
      // Kursordan pastdagi qism = konsol balandligi
      const pct = ((rect.bottom - ev.clientY) / rect.height) * 100;
      setConsoleHeight(Math.min(80, Math.max(12, pct)));
    };
    const onUp = () => {
      vDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  const pollSubmission = useCallback(
    async (id: number) => {
      for (let i = 0; i < 30; i++) {
        await new Promise((r) => setTimeout(r, 1000));
        const res = await submissionsApi.getById(id);
        const s = res.data;
        if (s.status !== 'PENDING' && s.status !== 'RUNNING') {
          setResult({ status: s.status, runtimeMs: s.runtimeMs, message: s.errorMessage, resultDiff: s.resultDiff });
          return;
        }
      }
      push(t('problem.submitTimeout'), 'error');
    },
    [push, t],
  );

  const runJudge = useCallback(
    async (isSubmit: boolean) => {
      if (!problem) return;
      if (problem.locked) {
        push(t('problem.premiumLockedToast'), 'error');
        return;
      }
      if (!isAuthenticated) {
        push(t('problem.loginRequired'), 'error');
        return;
      }

      if (isSubmit) setSubmitting(true);
      else setRunning(true);
      setResult(null);

      try {
        if (isSubmit) {
          const res = inContest
            ? await contestsApi.submit(contestSlug!, problem.slug, code, dialect)
            : await problemsApi.submit(problem.slug, code, dialect);
          await pollSubmission(res.data.submissionId);
          setSubmitCount((n) => n + 1);
        } else {
          // Contest masalalari unpublished bo'lishi mumkin — run public path orqali ishlamasligi mumkin
          const res = await problemsApi.run(problem.slug, code, dialect);
          setResult({
            status: res.data.status,
            runtimeMs: res.data.runtimeMs,
            message: res.data.message,
            resultDiff: res.data.resultDiff,
          });
        }
      } catch (err) {
        push(getApiErrorMessage(err, t('auth.errorGeneric')), 'error');
      } finally {
        if (isSubmit) setSubmitting(false);
        else setRunning(false);
      }
    },
    [problem, code, dialect, isAuthenticated, pollSubmission, push, t, inContest, contestSlug],
  );

  const runCustomWith = useCallback(async (payload: CustomTablePayload[]) => {
    if (!problem || problem.locked || !isAuthenticated) return;
    setCustomRunning(true);
    setResult(null);
    try {
      const res = await problemsApi.runCustom(problem.slug, code, payload, dialect);
      setResult({
        status: res.data.status,
        runtimeMs: res.data.runtimeMs,
        message: res.data.message,
        resultDiff: res.data.resultDiff,
      });
    } catch (err) {
      push(getApiErrorMessage(err, t('auth.errorGeneric')), 'error');
    } finally {
      setCustomRunning(false);
    }
  }, [problem, code, dialect, isAuthenticated, push, t]);

  const runCustomData = useCallback(
    () => runCustomWith(customTablesToPayload(customTables)),
    [runCustomWith, customTables],
  );

  /** Yiqilgan test ma'lumotini custom-tables ga yuklab, joriy query ni shu ma'lumot
   *  bilan darhol ishga tushiradi (user tuzatgan query ni sinab ko'rishi uchun). */
  const runOnFailedData = useCallback(
    (setupSql: string) => {
      const tables = buildCustomTables(setupSql);
      setCustomTables(tables);
      setCustomOpen(true);
      void runCustomWith(customTablesToPayload(tables));
    },
    [runCustomWith],
  );

  const primaryTopic = problem ? pickPrimaryTopic(problem.topics) : undefined;

  useSeo({
    title: problem ? problemSeoTitle(problem.title) : 'SQL masala — SQLMaster.uz',
    description: problem ? problemSeoDescription(problem) : undefined,
    path: inContest ? undefined : `/problems/${slug}`,
    jsonLd: problem && !inContest ? problemStructuredData(problem) : undefined,
  });

  // Til almashganda shu masala qayta yuklanadi — kontent o'rnida qoladi (loading ekrani yo'q)
  if (loading && problem?.slug !== slug) {
    return <div className="flex h-full items-center justify-center text-content-secondary">{t('common.loading')}</div>;
  }
  if (notFound || !problem) return <NotFound />;

  const difficulty = mapDifficulty(problem.difficulty);
  const locked = problem.locked === true;
  // Yechim tahlili tabi faqat nashr qilingani bo'lsa (kontest ichida — hech qachon)
  const tabs: Tab[] = [
    'description',
    ...(problem.editorial && !inContest ? (['editorial'] as Tab[]) : []),
    ...(!inContest ? (['solutions'] as Tab[]) : []),
    'submissions',
    'discuss',
  ];
  const activeTab: Tab = tabs.includes(tab) ? tab : 'description';

  const leftPanel = (
    <div className="flex h-full flex-col">
      {/* Tabs */}
      <div className="flex shrink-0 items-center gap-1 overflow-x-auto border-b border-line px-2 no-scrollbar">
        {tabs.map((tb) => (
          <button key={tb} onClick={() => setTab(tb)} className={cn('tab-btn whitespace-nowrap', activeTab === tb && 'active')}>
            {t(`problem.tabs.${tb}`)}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-5">
        {activeTab === 'description' && (
          <div>
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <h1 className="text-xl font-bold">
                {problem.id}. {problem.title}
              </h1>
              <Link to={difficultyPath(problem.difficulty)} aria-label={t('problem.difficultyLink')}>
                <DifficultyBadge difficulty={difficulty} />
              </Link>
              {problem.isPremium && (
                <span className="inline-flex items-center gap-1 rounded bg-[var(--medium)]/15 px-2 py-0.5 text-xs font-semibold text-[var(--medium)]">
                  <Lock className="h-3 w-3" /> {t('problems.premium')}
                </span>
              )}
            </div>

            {(problem.topics.length > 0 || problem.companies.length > 0) && (
              <div className="mb-4 flex flex-wrap gap-1.5" aria-label={t('problem.topicsLabel')}>
                {problem.topics.map((tp) => {
                  const meta = topicBySlug(tp);
                  return meta ? (
                    <Link key={tp} to={topicPath(tp)} className="tag transition-colors hover:text-brand-600">
                      {l(meta.title)}
                    </Link>
                  ) : (
                    <span key={tp} className="tag">{tp}</span>
                  );
                })}
                {problem.companies.map((c) => (
                  <span key={c} className="tag">{c}</span>
                ))}
              </div>
            )}

            {locked ? (
              // Premium bo'lim: shart HTML ga chiqmaydi, faqat qulf bloki (JSON-LD paywall selektori)
              <div className={cn(PAYWALL_CLASS, 'rounded-lg border border-[var(--medium)]/30 bg-[var(--medium)]/10 p-6 text-center')}>
                <Lock className="mx-auto mb-3 h-10 w-10 text-[var(--medium)]" />
                <h2 className="mb-2 text-lg font-semibold">{t('problem.premiumLockedTitle')}</h2>
                <p className="mb-4 text-sm text-content-secondary">{t('problem.premiumLockedDesc')}</p>
                <Link to="/pricing" className="btn btn-primary inline-flex gap-2 px-5 py-2.5 text-sm font-semibold">
                  {t('problem.premiumCta')}
                </Link>
              </div>
            ) : (
              <>
                {problem.description && (
                  <MarkdownLite text={problem.description} className="mb-6" />
                )}

                {problem.sampleTestCases.length > 0 && (
                  <>
                    <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-content-muted">
                      {t('problem.sampleData')}
                    </h3>
                    <div className="space-y-5">
                      {problem.sampleTestCases.map((sample) => (
                        <SampleCase key={sample.id} sample={sample} />
                      ))}
                    </div>
                  </>
                )}
              </>
            )}

            {!inContest && related.length > 0 && (
              <section className="mt-8 border-t border-line pt-5">
                <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-content-muted">
                  {t('problem.relatedTitle')}
                </h2>
                <ul className="space-y-1.5">
                  {related.map((rp) => (
                    <li key={rp.slug} className="flex items-center justify-between gap-3">
                      <Link
                        to={`/problems/${rp.slug}`}
                        className="truncate text-sm font-medium text-content transition-colors hover:text-brand-600"
                      >
                        {rp.id}. {l({ uz: rp.titleUz, ru: rp.titleRu, en: rp.titleEn })}
                      </Link>
                      <DifficultyBadge difficulty={mapDifficulty(rp.difficulty)} />
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}

        {/* Bepul tahlil matni doim DOM da (prerender HTML → indekslanadi), tab yopiq bo'lsa yashirin.
            Premium tahlil faqat tab ochilganda yuklanadi. */}
        {problem.editorial?.contentMd && !inContest ? (
          <div hidden={activeTab !== 'editorial'}>
            <EditorialTab slug={problem.slug} initialContent={problem.editorial.contentMd} />
          </div>
        ) : (
          activeTab === 'editorial' && <EditorialTab slug={problem.slug} />
        )}

        {activeTab === 'solutions' && <SolutionsTab slug={problem.slug} />}

        {activeTab === 'submissions' && (
          <SubmissionsTab slug={problem.slug} isAuthenticated={isAuthenticated} refreshKey={submitCount} />
        )}

        {activeTab === 'discuss' && (
          <DiscussionTab slug={problem.slug} />
        )}
      </div>
    </div>
  );

  const availableDialects = problem.dialects.map((d) => d.dialect);

  const rightPanel = (
    <div className="flex h-full min-w-0 flex-col">
      {/* Editor toolbar */}
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-line px-3 py-2">
        <select
          value={dialect}
          onChange={(e) => setDialect(e.target.value as ApiSqlDialect)}
          className="select w-auto min-w-[9rem] py-1.5 text-sm"
          aria-label={t('problem.dialect')}
          disabled={locked || availableDialects.length === 0}
        >
          {(availableDialects.length > 0 ? availableDialects : (['POSTGRESQL'] as ApiSqlDialect[])).map(
            (d) => (
              <option key={d} value={d}>
                {DIALECT_LABEL[d]}
              </option>
            ),
          )}
        </select>

        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setCode('');
              setResult(null);
              push(t('toast.codeReset'), 'info');
            }}
            className="btn btn-ghost gap-1.5 px-2.5 py-1.5 text-sm"
            title={t('problem.reset')}
            disabled={locked}
          >
            <RotateCcw className="h-4 w-4" />
            <span className="hidden sm:inline">{t('problem.reset')}</span>
          </button>
          {locked ? (
            <Link to="/pricing" className="btn btn-primary gap-1.5 px-3 py-1.5 text-sm">
              <Lock className="h-4 w-4" />
              {t('problem.premiumCta')}
            </Link>
          ) : isAuthenticated ? (
            <>
              <Button variant="secondary" size="sm" loading={running} onClick={() => runJudge(false)}>
                <Play className="h-4 w-4" />
                {running ? t('problem.running') : t('problem.run')}
              </Button>
              <Button variant="success" size="sm" loading={submitting} onClick={() => runJudge(true)}>
                <Send className="h-4 w-4" />
                {submitting ? t('problem.submitting') : t('problem.submit')}
              </Button>
            </>
          ) : (
            <Link to="/login" className="btn btn-primary gap-1.5 px-3 py-1.5 text-sm">
              <LogIn className="h-4 w-4" />
              {t('problem.loginRequired')}
            </Link>
          )}
        </div>
      </div>

      {/* Editor */}
      <div className={cn('min-h-0 flex-1 border-b border-line', locked && 'pointer-events-none opacity-50')}>
        <ClientOnly
          fallback={
            <div className="flex h-full items-center justify-center text-content-muted">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          }
        >
          <SqlEditor
            value={locked ? t('problem.premiumEditorPlaceholder') : code}
            onChange={locked ? () => undefined : setCode}
            onRun={locked ? () => undefined : () => runJudge(false)}
          />
        </ClientOnly>
      </div>

      {/* Editor/konsol vertikal resizer */}
      <div
        className="panel-resizer-v"
        onMouseDown={startVDrag}
        role="separator"
        aria-orientation="horizontal"
      />

      {/* Console */}
      <div
        className="flex shrink-0 flex-col overflow-hidden"
        style={{ height: `${consoleHeight}%` }}
      >
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-line px-4 py-2 text-sm font-medium text-content-secondary">
          <span>{t('problem.console')}</span>
          {isAuthenticated && !locked && (
            <button
              onClick={() => {
                const next = !customOpen;
                setCustomOpen(next);
                // Custom sinash rejimiga o'tganda oldingi oddiy Run natijasini tozalaymiz —
                // aks holda "Sizning natijangiz" jadvali eski natija bilan qolib ketadi.
                if (next) setResult(null);
              }}
              className="btn btn-ghost gap-1.5 px-2 py-1 text-xs"
              aria-expanded={customOpen}
            >
              <Database className="h-3.5 w-3.5" />
              {t('problem.customData')}
              <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', customOpen && 'rotate-180')} />
            </button>
          )}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {customOpen && (
            <CustomDataPanel
              tables={customTables}
              onChange={setCustomTables}
              onRun={runCustomData}
              onReset={() =>
                setCustomTables(buildCustomTables(problem.sampleTestCases?.[0]?.setupSql))
              }
              running={customRunning}
            />
          )}
          {!result ? (
            <p className="text-sm text-content-muted">{t('problem.runToSee')}</p>
          ) : (
            <ResultPanel result={result} onRunWithData={isAuthenticated && !locked ? runOnFailedData : undefined} />
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-full flex-col">
      {/* Breadcrumb bar */}
      <div className="flex shrink-0 items-center gap-2 border-b border-line px-4 py-2">
        {inContest ? (
          <Link to={`/contests/${contestSlug}`} className="btn btn-ghost gap-1 px-2 py-1.5 text-sm">
            <ChevronLeft className="h-4 w-4" />
            {t('problem.back')}
          </Link>
        ) : (
          <nav aria-label={t('problem.breadcrumb')} className="min-w-0 py-1.5 text-sm">
            <ol className="flex min-w-0 items-center gap-1.5 whitespace-nowrap text-content-secondary">
              <li>
                <Link to="/" className="transition-colors hover:text-content">
                  {t('nav.home')}
                </Link>
              </li>
              <li aria-hidden>
                <ChevronRight className="h-3.5 w-3.5" />
              </li>
              {/* JSON-LD BreadcrumbList bilan bir xil tuzilma (lib/structuredData problemCrumbs) */}
              {primaryTopic ? (
                <>
                  <li>
                    <Link to={TOPICS_INDEX_PATH} className="transition-colors hover:text-content">
                      {t('nav.topics')}
                    </Link>
                  </li>
                  <li aria-hidden>
                    <ChevronRight className="h-3.5 w-3.5" />
                  </li>
                  <li>
                    <Link to={topicPath(primaryTopic.slug)} className="transition-colors hover:text-content">
                      {l(primaryTopic.title)}
                    </Link>
                  </li>
                </>
              ) : (
                <li>
                  <Link to="/problems" className="transition-colors hover:text-content">
                    {t('nav.problems')}
                  </Link>
                </li>
              )}
              <li aria-hidden>
                <ChevronRight className="h-3.5 w-3.5" />
              </li>
              <li aria-current="page" className="min-w-0 truncate text-content">
                {problem.title}
              </li>
            </ol>
          </nav>
        )}
      </div>

      {/* Bitta DOM: mobil — ustma-ust (skroll), lg — split pane. Panellar ikki marta
          render qilinmaydi (bitta <h1>, bitta Monaco). */}
      <div
        ref={containerRef}
        className="min-h-0 flex-1 overflow-y-auto lg:flex lg:overflow-hidden"
        style={{ '--left-w': `${leftWidth}%` } as CSSProperties}
      >
        <div className="h-[45vh] overflow-hidden border-b border-line lg:h-auto lg:w-[var(--left-w)] lg:min-w-0 lg:border-b-0 lg:border-r">
          {leftPanel}
        </div>
        <div
          className="panel-resizer hidden lg:block"
          onMouseDown={startDrag}
          role="separator"
          aria-orientation="vertical"
        />
        <div className="h-[65vh] lg:h-auto lg:min-w-0 lg:flex-1">{rightPanel}</div>
      </div>
    </div>
  );
}

function SubmissionsTab({
  slug,
  isAuthenticated,
  refreshKey,
}: {
  slug: string;
  isAuthenticated: boolean;
  refreshKey: number;
}) {
  const { t } = useTranslation();
  const { lang } = useLocalized();
  const [items, setItems] = useState<SubmissionResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);

    problemsApi
      .mySubmissions(slug)
      .then((res) => {
        if (!cancelled) setItems(res.data.content);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug, isAuthenticated, refreshKey]);

  if (!isAuthenticated) {
    return (
      <div className="py-12 text-center">
        <p className="mb-4 text-content-secondary">{t('problem.loginToSeeSubmissions')}</p>
        <Link to="/login" className="btn btn-primary px-5 py-2.5 text-sm">
          {t('nav.login')}
        </Link>
      </div>
    );
  }

  if (loading) {
    return <div className="py-12 text-center text-content-secondary">{t('common.loading')}</div>;
  }
  if (error) {
    return <div className="py-12 text-center text-[var(--error)]">{t('problem.submissionsError')}</div>;
  }
  if (items.length === 0) {
    return <div className="py-12 text-center text-content-secondary">{t('problem.noSubmissions')}</div>;
  }

  return (
    <div className="space-y-2">
      {items.map((s) => {
        const verdict = (s.status in verdictIcon ? s.status : 'RUNTIME_ERROR') as Verdict;
        const Icon = verdictIcon[verdict];
        const isOpen = openId === s.id;
        return (
          <div key={s.id} className="overflow-hidden rounded-DEFAULT border border-line">
            <button
              onClick={() => setOpenId(isOpen ? null : s.id)}
              className="flex w-full items-center gap-3 bg-surface px-3 py-2.5 text-left transition-colors hover:bg-surface-hover"
            >
              <Icon className="h-4 w-4 shrink-0" style={{ color: verdictColor[verdict] }} />
              <span className="font-medium" style={{ color: verdictColor[verdict] }}>
                {t(`problem.verdict.${s.status.toLowerCase()}`, s.status)}
              </span>
              <span className="ml-auto flex items-center gap-3 text-xs text-content-muted">
                <span>{DIALECT_LABEL[s.dialect]}</span>
                {s.runtimeMs != null && <span className="tnum">{s.runtimeMs} ms</span>}
                <span className="hidden sm:inline">{timeAgo(s.createdAt, lang)}</span>
              </span>
            </button>

            {isOpen && (
              <div className="border-t border-line p-3">
                <pre className="overflow-x-auto rounded-DEFAULT bg-[#0d1117] p-3 font-mono text-[0.8125rem] leading-relaxed text-slate-200">
                  {s.sourceCode}
                </pre>
                {(() => {
                  const summary = getDatasetSummary(s.resultDiff);
                  if (summary) return <div className="mt-2"><DatasetChecklist summary={summary} details={getFailedDetails(s.resultDiff)} /></div>;
                  return s.errorMessage ? (
                    <p className="mt-2 text-sm text-[var(--error)]">{s.errorMessage}</p>
                  ) : null;
                })()}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SampleCase({ sample }: { sample: SampleTestCaseResponse }) {
  const { t } = useTranslation();
  const expected = rowsToQueryResult(sample.expectedResult);
  return (
    <div className="rounded-DEFAULT border border-line bg-surface p-3">
      <div className="mb-2 text-xs font-semibold text-content-muted">{sample.name}</div>
      {sample.setupSql && (
        <div className="mb-3">
          <SampleTables setupSql={sample.setupSql} />
        </div>
      )}
      {expected && (
        <div>
          <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-content-muted">
            {t('problem.expected')}
          </div>
          <ResultTable result={expected} />
        </div>
      )}
    </div>
  );
}

function CustomDataPanel({
  tables,
  onChange,
  onRun,
  onReset,
  running,
}: {
  tables: CustomTableState[];
  onChange: (tables: CustomTableState[]) => void;
  onRun: () => void;
  onReset: () => void;
  running: boolean;
}) {
  const { t } = useTranslation();

  const setCell = (ti: number, ri: number, ci: number, value: string) => {
    onChange(
      tables.map((tb, i) =>
        i !== ti
          ? tb
          : { ...tb, rows: tb.rows.map((row, r) => (r !== ri ? row : row.map((c, k) => (k !== ci ? c : value)))) },
      ),
    );
  };

  const addRow = (ti: number) => {
    onChange(
      tables.map((tb, i) => (i !== ti ? tb : { ...tb, rows: [...tb.rows, tb.columns.map(() => '')] })),
    );
  };

  const removeRow = (ti: number, ri: number) => {
    onChange(
      tables.map((tb, i) => (i !== ti ? tb : { ...tb, rows: tb.rows.filter((_, r) => r !== ri) })),
    );
  };

  return (
    <div className="mb-4 rounded-DEFAULT border border-line bg-surface p-3">
      <p className="mb-3 text-xs text-content-secondary">{t('problem.customDataHint')}</p>

      <div className="space-y-4">
        {tables.map((table, ti) => (
          <div key={table.name} className="overflow-hidden rounded border border-line">
            <div className="flex items-center gap-2 border-b border-line bg-elevated px-3 py-1.5">
              <Table2 className="h-3.5 w-3.5 text-[var(--brand-secondary)]" />
              <span className="font-mono text-xs font-semibold">{table.name}</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr className="bg-surface">
                    {table.columns.map((col) => (
                      <th key={col.name} className="border-b border-line px-2 py-1 text-left font-medium">
                        <span className="font-mono">{col.name}</span>{' '}
                        <span className="font-normal text-content-muted">{col.type}</span>
                      </th>
                    ))}
                    <th className="w-8 border-b border-line" />
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((row, ri) => (
                    <tr key={ri} className="group">
                      {table.columns.map((col, ci) => (
                        <td key={col.name} className="border-b border-line p-0">
                          <input
                            value={row[ci] ?? ''}
                            onChange={(e) => setCell(ti, ri, ci, e.target.value)}
                            spellCheck={false}
                            className="w-full min-w-[80px] bg-transparent px-2 py-1 font-mono text-xs outline-none focus:bg-elevated"
                            placeholder="NULL"
                          />
                        </td>
                      ))}
                      <td className="border-b border-line px-1 text-center">
                        <button
                          type="button"
                          onClick={() => removeRow(ti, ri)}
                          disabled={running || table.rows.length <= 1}
                          className="text-content-muted transition-colors hover:text-[var(--hard)] disabled:opacity-30"
                          title={t('problem.customRowDelete')}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              type="button"
              onClick={() => addRow(ti)}
              disabled={running}
              className="btn btn-ghost w-full justify-center gap-1.5 rounded-none border-t border-line py-1.5 text-xs"
            >
              <Plus className="h-3.5 w-3.5" />
              {t('problem.customRowAdd')}
            </button>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Button size="sm" loading={running} onClick={onRun} disabled={tables.length === 0}>
          <Play className="h-4 w-4" />
          {t('problem.customRun')}
        </Button>
        <button onClick={onReset} className="btn btn-ghost gap-1.5 px-2.5 py-1.5 text-sm" disabled={running}>
          <RotateCcw className="h-4 w-4" />
          {t('problem.customReset')}
        </button>
      </div>
    </div>
  );
}

function ResultPanel({
  result,
  onRunWithData,
}: {
  result: ConsoleResult;
  onRunWithData?: (setupSql: string) => void;
}) {
  const { t } = useTranslation();
  const verdict = (result.status in verdictIcon ? result.status : 'RUNTIME_ERROR') as Verdict;
  const Icon = verdictIcon[verdict];
  const color = verdictColor[verdict];
  const verdictKey = result.status.toLowerCase();

  const summary = getDatasetSummary(result.resultDiff);
  const details = getFailedDetails(result.resultDiff);

  return (
    <div className="animate-fade-in-fast">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center gap-1.5 font-semibold" style={{ color }}>
          <Icon className="h-5 w-5" />
          {t(`problem.verdict.${verdictKey}`, verdict)}
        </span>
        {result.runtimeMs != null && (
          <span className="text-xs text-content-muted">{t('problem.runtime', { ms: result.runtimeMs })}</span>
        )}
      </div>

      <p className="mb-3 text-sm text-content-secondary">
        {result.message || t(`problem.verdictMsg.${verdictKey}`, '')}
      </p>

      {summary ? (
        // Ko'p-dataset: yiqilgan test tafsiloti checklist ichida, ko'z tugmasi bilan ochiladi
        <DatasetChecklist summary={summary} details={details} onRunWithData={onRunWithData} />
      ) : (
        // Yagona-dataset (legacy): tafsilotni to'g'ridan-to'g'ri ko'rsatamiz
        details && (
          <div className="overflow-hidden rounded-DEFAULT border border-line">
            <FailedTestDetail details={details} onRunWithData={onRunWithData} />
          </div>
        )
      )}
    </div>
  );
}
